/**
 * components/ChatMessageList.js — Phase 4 Week 2
 *
 * Renders the conversation history with:
 *  • User bubbles (right-aligned, primary blue)
 *  • Assistant bubbles (left-aligned, surface card)
 *  • Animated streaming cursor while tokens arrive
 *  • Expandable "View SQL query used" disclosure per assistant turn
 *  • Error state styling (red tint, warning icon)
 *  • Auto-scrolls to bottom on new content
 *  • Dark mode via MUI palette tokens throughout
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Typography, Collapse, IconButton, Tooltip,
  CircularProgress, Paper,
} from '@mui/material';
import {
  SmartToy, DashboardCustomize, Person, WarningAmber, Code, ExpandMore, ExpandLess,
  BarChartOutlined,
} from '@mui/icons-material';
import ChartDialog from './ChartDialog';

// ── Streaming cursor ──────────────────────────────────────────────────────────

function StreamingCursor() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: '2px',
        height: '1em',
        bgcolor: 'primary.main',
        ml: '2px',
        verticalAlign: 'text-bottom',
        borderRadius: '1px',
        animation: 'hrms-blink 1s step-end infinite',
        '@keyframes hrms-blink': {
          '0%, 100%': { opacity: 1 },
          '50%':      { opacity: 0 },
        },
      }}
    />
  );
}

// ── SQL disclosure — DISABLED (comment-out to re-enable) ─────────────────────
// To re-enable: remove the /* and */ block comment delimiters below.
/*
function SqlDisclosure({ sql }) {
  const [open, setOpen] = useState(false);
  if (!sql) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          cursor: 'pointer', color: 'text.disabled',
          '&:hover': { color: 'primary.main' },
          transition: 'color 150ms',
        }}
      >
        <Code sx={{ fontSize: 13 }} />
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
          {open ? 'Hide' : 'View'} SQL query used
        </Typography>
        {open ? <ExpandLess sx={{ fontSize: 13 }} /> : <ExpandMore sx={{ fontSize: 13 }} />}
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 0.75,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)',
            border: '1px solid',
            borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            fontFamily: 'monospace',
            fontSize: '0.72rem',
            color: 'text.secondary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: 160,
            overflowY: 'auto',
          }}
        >
          {sql}
        </Box>
      </Collapse>
    </Box>
  );
}
*/ // END SqlDisclosure — DISABLED

// ── Single message bubble ─────────────────────────────────────────────────────

// Strip any code fences the LLM incorrectly includes (chart, json, plain ```)
function stripCodeBlocks(text) {
  if (!text) return text;
  return text
    .replace(/```[\w-]*\n[\s\S]*?```/g, '')   // fenced blocks with language tag
    .replace(/```[\s\S]*?```/g, '')            // fenced blocks without tag
    .replace(/\n{3,}/g, '\n\n')               // collapse excess blank lines left behind
    .trim();
}

function MessageBubble({ message }) {
  const isUser      = message.role === 'user';
  const isStreaming = message.streaming;
  const isError     = message.isError;
  const isCancelled = !message.isError && message.content === 'Response cancelled.';
  const isEmpty     = !message.content && isStreaming;
  const [chartOpen, setChartOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 2,
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: isUser
            ? 'primary.main'
            : isError
              ? 'error.light'
              : isCancelled
                ? 'action.disabledBackground'
                : (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(21,101,192,0.08)',
        }}
      >
        {isUser
          ? <Person sx={{ fontSize: 16, color: '#fff' }} />
          : isError
            ? <WarningAmber sx={{ fontSize: 16, color: 'error.main' }} />
            : <DashboardCustomize sx={{ fontSize: 16, color: 'primary.main' }} />
        }
      </Box>

      {/* Bubble */}
      <Box sx={{ maxWidth: '80%', minWidth: 60 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ display: 'block', mb: 0.4, textAlign: isUser ? 'right' : 'left', fontSize: '0.85rem' }}
        >
          {isUser ? 'You' : 'HRMS Assistant'}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: '10px 14px',
            borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
            bgcolor: isUser
              ? 'primary.main'
              : isError
                ? (t) => t.palette.mode === 'dark' ? 'rgba(198,40,40,0.15)' : '#FFEBEE'
                : (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F5F7FB',
            border: '1px solid',
            borderColor: isUser
              ? 'transparent'
              : isError
                ? 'error.light'
                : (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(21,101,192,0.08)',
          }}
        >
          {/* Loading spinner before first token */}
          {isEmpty ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} thickness={5} />
              <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.85rem' }}>
                Thinking…
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontSize: '1rem',
                lineHeight: 1.65,
                color: isUser ? '#fff' : isError ? 'error.main' : isCancelled ? 'text.disabled' : 'text.primary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {isUser ? message.content : stripCodeBlocks(message.content)}
              {isStreaming && !isEmpty && <StreamingCursor />}
            </Typography>
          )}
        </Paper>

        {/* SQL disclosure — DISABLED (remove comment delimiters below to re-enable) */}
        {/* {!isUser && !isStreaming && !isError && (
          <SqlDisclosure sql={message.sqlUsed} />
        )} */}

        {/* View Chart button + dialog — shown for completed analytics responses with chart data */}
        {!isUser && !isStreaming && !isError && message.chartType && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BarChartOutlined sx={{ fontSize: 15 }} />}
              onClick={() => setChartOpen(true)}
              sx={{
                mt: 0.75,
                fontSize: '0.85rem',
                borderRadius: 2,
                textTransform: 'none',
                borderColor: 'primary.light',
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.50' },
              }}
            >
              View Chart
            </Button>
            <ChartDialog
              open={chartOpen}
              onClose={() => setChartOpen(false)}
              chartType={message.chartType}
              chartData={message.chartData}
              chartTitle={message.chartTitle}
              yAxisLabel={message.yAxisLabel}
            />
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Main list ─────────────────────────────────────────────────────────────────

export default function ChatMessageList({ messages, isLoading }) {
  const bottomRef = useRef(null);

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!messages.length) {
    return (
      <Box
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 1.5, px: 3, color: 'text.disabled',
        }}
      >
        <DashboardCustomize sx={{ fontSize: 44, opacity: 0.25 }} />
        <Typography variant="body2" textAlign="center" color="text.disabled" sx={{ fontSize: '0.85rem' }}>
          Ask anything about your HR data.<br />
          Your questions stay within your access level.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} />
      ))}
      <div ref={bottomRef} />
    </Box>
  );
}
